// Resolve in place, then collapse. When an item leaves a list (approved, ignored, dismissed), it stays where it
// was for a moment in its resolved state, so you see what happened, and only then leaves; the list closes the gap
// with a layout animation. An item that is still in the list but done (an optimistic update) counts as leaving.
// Use it only for lists that show waiting things; a list that shows history passes isDone = () => false.
import { useEffect, useRef, useState } from 'react';

export const LINGER_MS = 900;

export function useLinger<T extends { id: string }>(items: T[], isDone: (t: T) => boolean, ms = LINGER_MS): T[] {
  const [now, setNow] = useState(() => Date.now());
  const ghosts = useRef(new Map<string, { item: T; index: number; until: number }>());
  const doneAt = useRef(new Map<string, number>());
  const prev = useRef<T[]>(items);

  // Record when each item resolved, and keep the ones that vanished from the data as ghosts in their old place.
  const t = Date.now();
  for (const i of items) if (isDone(i) && !doneAt.current.has(i.id)) doneAt.current.set(i.id, t);
  if (prev.current !== items) {
    prev.current.forEach((p, index) => {
      if (!items.some(i => i.id === p.id) && !ghosts.current.has(p.id)) {
        ghosts.current.set(p.id, { item: p, index, until: (doneAt.current.get(p.id) ?? t) + ms });
      }
    });
    prev.current = items;
  }

  const deadlines = [...ghosts.current.values()].map(g => g.until)
    .concat(items.filter(isDone).map(i => doneAt.current.get(i.id)! + ms))
    .filter(d => d > now);
  const nextDeadline = deadlines.length ? Math.min(...deadlines) : null;
  useEffect(() => {
    if (nextDeadline === null) return;
    const h = window.setTimeout(() => setNow(Date.now()), Math.max(0, nextDeadline - Date.now()) + 10);
    return () => window.clearTimeout(h);
  }, [nextDeadline]);

  const cut = Math.max(now, t - 1);
  for (const [id, g] of ghosts.current) if (g.until <= cut) ghosts.current.delete(id);
  const out = items.filter(i => !isDone(i) || doneAt.current.get(i.id)! + ms > cut);
  for (const g of [...ghosts.current.values()].sort((a, b) => a.index - b.index)) {
    if (!out.some(o => o.id === g.item.id)) out.splice(Math.min(g.index, out.length), 0, g.item);
  }
  return out;
}
